import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../api/client";
import { listAuthEvents, type AuthEventRow } from "../api/auth";

const EVENT_LABEL: Record<string, string> = {
  login_ok: "Signed in",
  login_ok_2fa: "Signed in (2FA)",
  login_ok_device_code: "Signed in from TV",
  login_2fa_recovery: "Signed in with recovery code",
  login_fail: "Sign-in failed",
  login_2fa_fail: "2FA code rejected",
  refresh: "Session refreshed",
  refresh_replay: "Refresh replay detected (device-wide revoke fired)",
  logout: "Signed out",
  logout_all: "Signed out everywhere",
  password_change: "Password changed",
  "2fa_enroll": "2FA enrolled",
  invite_create: "Invite created",
  invite_redeem: "Invite redeemed",
  server_pair: "Install paired",
  server_revoke: "Install revoked",
  device_revoke: "Device revoked",
};

const EVENT_CLASS: Record<string, string> = {
  login_fail: "warn",
  login_2fa_fail: "warn",
  refresh_replay: "danger",
};

export function ActivityPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState<AuthEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noMore, setNoMore] = useState(false);

  async function loadInitial() {
    setLoading(true);
    try {
      const r = await listAuthEvents(api, 100);
      setRows(r.events);
      if (r.events.length < 100) setNoMore(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "load failed");
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (rows.length === 0) return;
    const before = rows[rows.length - 1].ts;
    try {
      const r = await listAuthEvents(api, 100, before);
      setRows((prev) => [...prev, ...r.events]);
      if (r.events.length < 100) setNoMore(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "load failed");
    }
  }

  useEffect(() => { loadInitial(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="vstack">
      <div>
        <h1>Activity</h1>
        <p className="meta">Every authentication event logged for your account.</p>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        {loading && rows.length === 0 && <p className="meta">Loading…</p>}
        {!loading && rows.length === 0 && <p className="meta">No activity recorded yet.</p>}
        {rows.map((r) => {
          const label = EVENT_LABEL[r.event_type] ?? r.event_type;
          const cls = EVENT_CLASS[r.event_type];
          return (
            <div className="row" key={r.id}>
              <div>
                <div className={`row-title ${cls ?? ""}`}>{label}</div>
                <div className="row-meta">
                  {r.ts}
                  {r.ip ? ` · ${r.ip}` : ""}
                  {r.device_id ? ` · ${r.device_id.slice(0, 12)}…` : ""}
                </div>
                {r.detail && Object.keys(r.detail).length > 0 && (
                  <div className="row-meta subtle">{JSON.stringify(r.detail)}</div>
                )}
              </div>
            </div>
          );
        })}
        {!noMore && rows.length > 0 && (
          <div style={{ paddingTop: 16 }}>
            <button className="btn" onClick={loadMore}>Load more</button>
          </div>
        )}
      </div>
    </div>
  );
}
